import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  BackrsForm,
  TermsAndConditions,
  BackrsFormField,
  BackrsFormValues,
} from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/appRoutes.constants';

import { Box, Button, CircularProgress, SxProps, Theme, Typography } from '@mui/material';
import { type useForm } from 'react-hook-form';

import { OnboardingStepContainer } from './components/OnboardingStepContainer';
import { OnboardingStepHeader } from './components/OnboardingStepHeader';
import { OnboardingQuestion } from './onboarding.types';
import { StepKey } from './utils/stepNavigation';

type OnboardingQuestionViewProps = Readonly<{
  currentQuestion: OnboardingQuestion;
  fields: BackrsFormField[];
  formMethods: ReturnType<typeof useForm<BackrsFormValues>>;
  goToNextQuestion: (data: BackrsFormValues) => Promise<void>;
  handleClearEmptyFields: (data: BackrsFormValues) => BackrsFormValues;
  setCurrentStep: (step: StepKey) => void;
  getStepKeyForField: (fieldName: string) => StepKey | undefined;
  currentStep: StepKey;
}>;

export function OnboardingQuestionView({
  currentQuestion,
  fields,
  formMethods,
  goToNextQuestion,
  handleClearEmptyFields,
  setCurrentStep,
  getStepKeyForField,
  currentStep,
}: OnboardingQuestionViewProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const inviteCode = formMethods.watch('invite_code');
  const isContinueDisabled =
    isSubmitting || (currentStep === 'ACTIVATION_CODE' && !inviteCode?.trim());

  /**
   * If there is an error returned from the API, navigate back to the step that contains the field with the error.
   * Ex: if email is already in use and is on COMPLETE_ACCOUNT step, the API returns an error with the email field.
   * We will then navigate to COMPLETE_ACCOUNT and display the error with RHF.
   */
  const handleErrorNavigation = (error: unknown) => {
    if (error && typeof error === 'object' && 'data' in error) {
      const typedError = error as { data: Record<string, string> };

      const errorFields = Object.keys(typedError.data);

      const firstFieldWithError = errorFields[0];

      if (firstFieldWithError) {
        const targetStep = getStepKeyForField(firstFieldWithError);

        if (targetStep !== undefined) {
          // Store the error data to be set after navigation
          const errorData = errorFields.reduce(
            (acc, field) => {
              acc[field] = typedError.data[field];

              return acc;
            },
            {} as Record<string, string>,
          );

          // Navigate first (if not already on the correct step)
          if (currentStep !== targetStep) {
            setCurrentStep(targetStep);
          }

          // Set errors after navigation completes
          // Use a helper function that can be retried to handle async state updates
          const setErrors = () => {
            Object.entries(errorData).forEach(([field, message]) => {
              if (message) {
                formMethods.setError(field as keyof BackrsFormValues, {
                  type: 'manual',
                  message: message,
                });
              }
            });
          };

          requestAnimationFrame(() => {
            setErrors();
            setTimeout(() => {
              setErrors();
            }, 100);
          });
        }
      }
    }
  };

  return (
    <OnboardingStepContainer>
      <OnboardingStepHeader
        iconName={currentQuestion.featuredIcon}
        title={currentQuestion.title}
        subtitle={currentQuestion.subTitle}
      />
      <Box sx={questionContainerStyle}>
        <BackrsForm
          fields={fields}
          methods={formMethods}
          onSubmit={async (data) => {
            setIsSubmitting(true);
            try {
              await goToNextQuestion(handleClearEmptyFields(data));
            } catch (error) {
              handleErrorNavigation(error);
            } finally {
              setIsSubmitting(false);
            }
          }}
        >
          <Box sx={buttonContainerSx}>
            <Button
              id="onboarding-continue-button"
              type="submit"
              fullWidth
              hierarchy="Primary"
              size="lg"
              disabled={isContinueDisabled}
              endIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : undefined}
            >
              Continue
            </Button>
          </Box>
        </BackrsForm>

        {currentQuestion.subAction && (
          <Box sx={subActionContainerStyle}>
            <Typography variant="text-sm-regular">{currentQuestion.subAction.body}</Typography>
            <Typography
              id="onboarding-sub-action-link"
              variant="text-sm-semibold"
              component="a"
              onClick={
                currentQuestion.subAction.onClick === 'LOGIN'
                  ? () => navigate(APP_ROUTE.LOGIN)
                  : currentQuestion.subAction.onClick === 'SKIP'
                    ? async () => {
                        setIsSubmitting(true);
                        try {
                          await goToNextQuestion({});
                        } catch (error) {
                          handleErrorNavigation(error);
                        } finally {
                          setIsSubmitting(false);
                        }
                      }
                    : () => {
                        formMethods.handleSubmit(async (data) => {
                          try {
                            await goToNextQuestion(handleClearEmptyFields(data));
                          } catch (error) {
                            handleErrorNavigation(error);
                          }
                        })();
                      }
              }
              sx={subActionLink}
            >
              {currentQuestion.subAction.actionText}
            </Typography>
          </Box>
        )}
      </Box>

      {currentStep === 'INVITE_FAMILY' && <TermsAndConditions />}
    </OnboardingStepContainer>
  );
}

const subActionContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
  mt: backrs.spacing['spacing-3xl'],
  justifyContent: 'center',
  width: '100%',
});

const subActionLink: SxProps<Theme> = ({ backrs }) => ({
  cursor: 'pointer',
  color: backrs.colors['Colors/Text/text-brand-secondary'],
});

const questionContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  minWidth: { sm: '360px' },
  maxWidth: { sm: '360px' },
  width: { xs: '90vw', sm: 'unset' },
  mt: `-${backrs.spacing['spacing-2xl']}`,
});

const buttonContainerSx: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
};
